import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { LandingNav } from "@/components/landing/LandingNav";
import { Container, ContourField, Eyebrow } from "@/components/landing/primitives";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";

export interface LegalTocItem {
  /** The id of the section the entry jumps to. */
  id: string;
  label: string;
}

const tocLink =
  "block rounded-sm py-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary";

function TocList({ items }: { items: readonly LegalTocItem[] }) {
  return (
    <ol className="space-y-0.5 text-[13px] leading-snug">
      {items.map((item) => (
        <li key={item.id}>
          <a href={`#${item.id}`} className={tocLink}>
            {item.label}
          </a>
        </li>
      ))}
    </ol>
  );
}

/**
 * The public site's frame for a legal document (terms, privacy, cookies, DPA):
 * the landing page's nav and footer, a quiet contour header with the title and
 * the version or effective-date line, and the text in a ~70ch column with a
 * table of contents (sticky beside it on wide screens, folded above it on
 * phones). Printing drops everything but the document.
 *
 * Presentation only: every word comes from the page, which takes it from
 * src/lib/legal/** unchanged.
 */
export function LegalLayout({
  locale,
  path,
  title,
  subtitle,
  effective,
  toc,
  children,
}: {
  locale: LandingLocale;
  /** The page's public path ("/privacy"), for the nav's language switch and the footer. */
  path: string;
  title: string;
  subtitle?: string;
  /** The version or effective-date line, shown under the title. */
  effective?: string;
  toc?: readonly LegalTocItem[];
  children: ReactNode;
}) {
  const tr = makeTr(locale);
  const contents = tr("Obsah", "Contents");

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased print:bg-white print:text-black"
    >
      <div className="contents print:hidden">
        <LandingNav locale={locale} path={path} />
      </div>

      <main id="obsah">
        <section
          aria-labelledby="legal-title"
          className="relative isolate overflow-hidden print:overflow-visible"
        >
          <ContourField
            variant="hero"
            className="opacity-[0.14] dark:opacity-[0.08] print:hidden"
          />
          <Container className="pb-10 pt-14 sm:pb-14 sm:pt-20 print:max-w-none print:px-0 print:pb-6 print:pt-0 print:[&_*]:!text-black">
            <Eyebrow className="mb-3 print:hidden">
              {tr("Právní dokumenty", "Legal documents")}
            </Eyebrow>
            <h1
              id="legal-title"
              className="max-w-3xl text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl print:text-3xl"
            >
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg print:text-black">
                {subtitle}
              </p>
            )}
            {effective && (
              <p className="mt-6 inline-flex max-w-full items-center gap-2 rounded-xl border bg-background/80 px-3 py-1.5 text-[13px] font-medium sm:rounded-full sm:py-1 print:mt-3 print:border-0 print:bg-transparent print:px-0 print:text-black">
                <span
                  aria-hidden="true"
                  className="size-[7px] shrink-0 rounded-full bg-[hsl(var(--dot-green))] print:hidden"
                />
                {effective}
              </p>
            )}
          </Container>
        </section>

        <Container
          className={
            toc && toc.length > 0
              ? "pb-20 sm:pb-28 lg:grid lg:grid-cols-[13rem_minmax(0,70ch)] lg:gap-14 print:block print:max-w-none print:px-0"
              : "pb-20 sm:pb-28 print:max-w-none print:px-0"
          }
        >
          {toc && toc.length > 0 && (
            <>
              <nav aria-label={contents} className="hidden lg:block print:hidden">
                <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto border-l pl-4">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">{contents}</p>
                  <TocList items={toc} />
                </div>
              </nav>
              <details className="group mb-10 rounded-xl border bg-secondary/40 lg:hidden print:hidden">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium [&::-webkit-details-marker]:hidden">
                  {contents}
                  <ChevronDown
                    aria-hidden="true"
                    className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
                  />
                </summary>
                <div className="border-t px-4 py-3">
                  <TocList items={toc} />
                </div>
              </details>
            </>
          )}
          <article className="max-w-[70ch] text-[15px] leading-7 text-foreground/85 sm:text-base sm:leading-7 print:max-w-none print:text-[11pt] print:leading-normal print:text-black print:[&_*]:!bg-transparent print:[&_*]:!text-black print:[&_a]:no-underline [&_h2]:scroll-mt-24 [&_section]:scroll-mt-24">
            {children}
          </article>
        </Container>
      </main>

      <div className="contents print:hidden">
        <LandingFooter locale={locale} path={path} />
      </div>
    </div>
  );
}
